import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useNavigate, useOutletContext, useSearchParams } from "react-router-dom"
import moment from "moment/moment"
import nProgress from "nprogress"
import axios from "axios"
import { useAuth } from "contexts/auth-context"
import { Pagination } from "components"
import { lang } from "config"
import useDebouncedSearchParam from "hooks/use-debounced-search-param"

const API = process.env.REACT_APP_INVENTORY_API_URL

function prettyStatus(status) {
  const s = String(status ?? "").toUpperCase()
  if (s === "PENDING") return "Pending"
  if (s === "APPROVED") return "Approved"
  if (s === "REJECTED") return "Rejected"
  if (s === "CANCELLED") return "Cancelled"
  if (s === "SUBMITTED") return "Submitted"
  if (s === "DRAFT") return "Draft"
  return status ?? "-"
}

function statusColor(status) {
  const s = String(status ?? "").toUpperCase()
  switch (s) {
    case "APPROVED":
      return "bg-green-100 text-green-700"
    case "REJECTED":
      return "bg-red-100 text-red-700"
    case "CANCELLED":
      return "bg-gray-100 text-gray-600"
    case "PENDING":
    case "SUBMITTED":
      return "bg-yellow-100 text-yellow-700"
    default:
      return "bg-neutral-100 text-neutral-700"
  }
}

function stepBadge(stepNo) {
  if (!stepNo) return "Done"
  return `Step ${stepNo}`
}

/**
 * NOTE:
 * - inbox API kita saat ini return array (bukan paginate).
 * - Supaya tetap ada Pagination UI seperti contoh kamu, kita bikin pagination client-side.
 */
function paginateClient(rows, page = 1, perPage = 20) {
  const p = Math.max(1, Number(page) || 1)
  const pp = Math.max(1, Number(perPage) || 20)
  const total = rows.length
  const lastPage = Math.max(1, Math.ceil(total / pp))
  const startIdx = (p - 1) * pp
  const endIdx = startIdx + pp
  const data = rows.slice(startIdx, endIdx)

  // Shape mirip laravel paginate
  return {
    data,
    total,
    per_page: pp,
    current_page: p,
    last_page: lastPage,
    from: total ? startIdx + 1 : 0,
    to: total ? Math.min(endIdx, total) : 0,
    links: buildLinks(p, lastPage),
  }
}

function buildLinks(current, last) {
  // minimal links seperti komponen Pagination kamu biasanya butuh array {url,label,active}
  const links = []
  links.push({ url: current > 1 ? `?page=${current - 1}` : null, label: "&laquo; Previous", active: false })
  for (let i = 1; i <= last; i++) {
    // batasi biar ga kepanjangan
    if (i === 1 || i === last || Math.abs(i - current) <= 2) {
      links.push({ url: `?page=${i}`, label: String(i), active: i === current })
    } else if (
      links.length &&
      links[links.length - 1].label !== "..."
    ) {
      links.push({ url: null, label: "...", active: false })
    }
  }
  links.push({ url: current < last ? `?page=${current + 1}` : null, label: "Next &raquo;", active: false })
  return links
}

/* =========================
 * API helpers
 * ========================= */
async function apiInbox({ actor_name, outlet_id, module }) {
  return axios
    .get(`${API}/approvals/inbox`, { params: { actor_name, outlet_id, module } })
    .then((r) => r.data)
}

async function apiApprovalDetail(id) {
  return axios.get(`${API}/approvals/${id}`).then((r) => r.data)
}

async function apiApprove({ id, actor_name, note }) {
  return axios.post(`${API}/approvals/${id}/approve`, { actor_name, note }).then((r) => r.data)
}

async function apiReject({ id, actor_name, note }) {
  return axios.post(`${API}/approvals/${id}/reject`, { actor_name, note }).then((r) => r.data)
}

async function apiCancel({ id, actor_name, note }) {
  return axios.post(`${API}/approvals/${id}/cancel`, { actor_name, note }).then((r) => r.data)
}

export default function ApprovalList() {
  const { currentBranch, user } = useAuth()
  const { transition, setTransition } = useOutletContext()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  const searchInput = useRef()

  const page = searchParams.get("page") || 1
  const search = searchParams.get("search") || ""
  const [searchTemp, setSearchTemp] = useDebouncedSearchParam({ search, searchParams, setSearchParams })
  const status = searchParams.get("status") || ""
  const type = searchParams.get("type") || "" // approvable_type filter (opsional)
  const outletId = currentBranch ? currentBranch.id : (searchParams.get("outlet_id") || "")

  // local state data
  const [rows, setRows] = useState([])
  const [isLoading, setIsLoading] = useState(false)

  // -------------------- modal detail state --------------------
  const [isDetailOpen, setIsDetailOpen] = useState(false)
  const [selectedId, setSelectedId] = useState(null)
  const [detail, setDetail] = useState(null)
  const [loadingDetail, setLoadingDetail] = useState(false)
  const [actionLoading, setActionLoading] = useState(false)

  const actorName = useMemo(() => {
    // fallback: user?.name kalau ada di auth context kamu
    return user?.name || user?.username || "SYSTEM"
  }, [user])

  const fetchInbox = useCallback(async () => {
    setIsLoading(true)
    try {
      const res = await apiInbox({
        actor_name: actorName,
        outlet_id: outletId || undefined,
        module: "inventory",
      })
      const list = res?.data ?? []
      setRows(Array.isArray(list) ? list : [])
    } catch (e) {
      console.error(e)
      setRows([])
    } finally {
      setIsLoading(false)
    }
  }, [actorName, outletId])

  // progress bar
  useEffect(() => {
    if (isLoading) nProgress.start()
    else nProgress.done()
  }, [isLoading])

  useEffect(() => {
    window.history.replaceState({}, document.title)
    if (transition?.reverse) setTransition({ type: "slide", reverse: false })
  }, [])

  useEffect(() => {
    fetchInbox()
  }, [fetchInbox])

  useEffect(() => {
    if (searchInput.current) searchInput.current.focus()
  }, [])

  const filteredRows = useMemo(() => {
    let list = rows

    if (status) {
      const st = String(status).toUpperCase()
      list = list.filter((r) => String(r.status ?? "").toUpperCase() === st)
    }

    if (type) {
      list = list.filter((r) => String(r.approvable_type ?? "") === String(type))
    }

    if (search) {
      const q = String(search).toLowerCase()
      list = list.filter((r) => {
        const hay = [
          r.request_number,
          r.title,
          r.summary,
          r.requested_by_name,
          r.approvable_id,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
        return hay.includes(q)
      })
    }

    return list
  }, [rows, status, type, search])

  const paged = useMemo(() => paginateClient(filteredRows, page, 20), [filteredRows, page])

  const updateFilter = (value) => {
    setSearchParams(value)
  }

  const removeFilter = () => {
    setSearchParams({})
  }

  const openDetail = useCallback(async (id) => {
    setIsDetailOpen(true)
    setSelectedId(id)
    setDetail(null)
    setLoadingDetail(true)

    try {
      const res = await apiApprovalDetail(id)
      setDetail(res?.data ?? null)
    } catch (e) {
      console.error(e)
      setDetail(null)
    } finally {
      setLoadingDetail(false)
    }
  }, [])

  const closeDetail = useCallback(() => {
    setIsDetailOpen(false)
    setSelectedId(null)
    setDetail(null)
    setActionLoading(false)
  }, [])

  const canAction = useMemo(() => {
    const s = String(detail?.status ?? "").toUpperCase()
    return s === "PENDING" || s === "SUBMITTED"
  }, [detail])

  const onApprove = useCallback(async () => {
    if (!detail?.id) return
    const ok = window.confirm("Approve approval ini?")
    if (!ok) return

    setActionLoading(true)
    try {
      await apiApprove({ id: detail.id, actor_name: actorName, note: "Approved from CMS" })
      await fetchInbox()
      closeDetail()
    } catch (e) {
      console.error(e)
      alert("Gagal approve.")
    } finally {
      setActionLoading(false)
    }
  }, [detail, actorName, fetchInbox, closeDetail])

  const onReject = useCallback(async () => {
    if (!detail?.id) return
    const reason = window.prompt("Alasan reject?", "Rejected from CMS")
    if (!reason) return

    const ok = window.confirm("Reject approval ini?")
    if (!ok) return

    setActionLoading(true)
    try {
      await apiReject({ id: detail.id, actor_name: actorName, note: reason })
      await fetchInbox()
      closeDetail()
    } catch (e) {
      console.error(e)
      alert("Gagal reject.")
    } finally {
      setActionLoading(false)
    }
  }, [detail, actorName, fetchInbox, closeDetail])

  const onCancel = useCallback(async () => {
    if (!detail?.id) return
    const ok = window.confirm("Batalkan approval ini?")
    if (!ok) return

    setActionLoading(true)
    try {
      await apiCancel({ id: detail.id, actor_name: actorName, note: "Cancelled from CMS" })
      await fetchInbox()
      closeDetail()
    } catch (e) {
      console.error(e)
      alert("Gagal cancel.")
    } finally {
      setActionLoading(false)
    }
  }, [detail, actorName, fetchInbox, closeDetail])

  // opsi filter type dari data yang ada
  const typeOptions = useMemo(() => {
    const set = new Set()
    for (const r of rows) {
      if (r?.approvable_type) set.add(String(r.approvable_type))
    }
    return Array.from(set)
  }, [rows])

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between text-xs">
        <div className="flex items-center sm:space-x-2">
          <div className="relative hidden sm:block">
            <div className="absolute inset-y-0 flex items-center pl-3 pointer-events-none">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" width={24} height={24} viewBox="0 0 24 24" strokeWidth={1} stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                <circle cx={10} cy={10} r={7}></circle>
                <line x1={21} y1={21} x2={15} y2={15}></line>
              </svg>
            </div>
            <input
              ref={searchInput}
              onChange={(e) => setSearchTemp(e.target.value)}
              value={searchTemp}
              type="text"
              placeholder={`${lang.search} Approvals`}
              autoComplete="off"
              className="w-64 py-3 pl-8 pr-4 text-xs transition border border-neutral-200 focus:outline-none rounded-xl focus:border-neutral-400 focus:ring focus:ring-neutral-200"
            />
          </div>

          {/* Filter sederhana (status + type) */}
          <div className="flex items-center gap-2">
            <select
              value={status}
              onChange={(e) => setSearchParams({ ...Object.fromEntries(searchParams.entries()), status: e.target.value, page: 1 })}
              className="py-2 px-3 text-xs border border-neutral-200 rounded-xl bg-white"
            >
              <option value="">All Status</option>
              <option value="PENDING">Pending</option>
              <option value="APPROVED">Approved</option>
              <option value="REJECTED">Rejected</option>
              <option value="CANCELLED">Cancelled</option>
            </select>

            <select
              value={type}
              onChange={(e) => setSearchParams({ ...Object.fromEntries(searchParams.entries()), type: e.target.value, page: 1 })}
              className="py-2 px-3 text-xs border border-neutral-200 rounded-xl bg-white"
            >
              <option value="">All Type</option>
              {typeOptions.map((t) => (
                <option key={t} value={t}>{t.split("\\").pop()}</option>
              ))}
            </select>

            <button
              type="button"
              onClick={removeFilter}
              className="py-2 px-3 text-xs rounded-xl border border-neutral-200 hover:bg-neutral-50"
            >
              Reset
            </button>

            <button
              type="button"
              onClick={fetchInbox}
              className="py-2 px-3 text-xs rounded-xl border border-neutral-200 hover:bg-neutral-50"
            >
              Refresh
            </button>
          </div>
        </div>

        <div className="text-xs text-neutral-500">
          Outlet: <span className="text-neutral-900 font-medium">{outletId || "-"}</span>
        </div>
      </div>

      <div className="overflow-x-auto border border-neutral-200 rounded-xl">
        <table className="min-w-full overflow-x-auto divide-y divide-neutral-200">
          <thead className="bg-neutral-50 rounded-t-3xl">
            <tr>
              <th className="px-6 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">Request No</th>
              <th className="px-6 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">Title</th>
              <th className="px-6 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">Type</th>
              <th className="px-6 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">Requester</th>
              <th className="px-6 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">Step</th>
              <th className="px-6 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">Status</th>
              <th className="px-6 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">{lang.created_at}</th>
              <th className="relative px-6 py-3"><span className="sr-only">{lang.action}</span></th>
            </tr>
          </thead>

          <tbody className="bg-white divide-y divide-neutral-200">
            {isLoading && (
              <tr className="text-center">
                <td colSpan="10" className="px-6 py-4 text-xs text-neutral-500 whitespace-nowrap">
                  {lang.loading_data}
                </td>
              </tr>
            )}

            {!isLoading && paged.data.length === 0 && !search && (
              <tr className="text-center">
                <td colSpan="10" className="px-6 py-4 text-xs text-neutral-500 whitespace-nowrap">
                  {lang.no_data}
                </td>
              </tr>
            )}

            {!isLoading && paged.data.length === 0 && search && (
              <tr className="text-center">
                <td colSpan="10" className="px-6 py-4 text-xs text-neutral-500 whitespace-nowrap">
                  {lang.no_result}
                </td>
              </tr>
            )}

            {!isLoading && paged.data.length > 0 && paged.data.map((row) => (
              <tr key={row.id} onClick={() => openDetail(row.id)} className="cursor-pointer hover:bg-neutral-50">
                <td className="px-6 py-4 text-xs text-neutral-700 whitespace-nowrap">
                  <div className="flex flex-col space-y-1">
                    <div className="font-medium text-neutral-900">{row.request_number ?? "-"}</div>
                    <div className="text-neutral-500">{row.approvable_id ? `Doc: ${row.approvable_id}` : ""}</div>
                  </div>
                </td>

                <td className="px-6 py-4 text-xs font-medium text-neutral-900 whitespace-nowrap">
                  {row.title ?? "-"}
                </td>

                <td className="px-6 py-4 text-xs text-neutral-700 whitespace-nowrap">
                  {String(row.approvable_type ?? "").split("\\").pop() || "-"}
                </td>

                <td className="px-6 py-4 text-xs text-neutral-700 whitespace-nowrap">
                  {row.requested_by_name ?? "-"}
                </td>

                <td className="px-6 py-4 text-xs text-neutral-700 whitespace-nowrap">
                  <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold bg-neutral-100 text-neutral-700">
                    {stepBadge(row.current_step_no)}
                  </span>
                </td>

                <td className="px-6 py-4 text-xs">
                  <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${statusColor(row.status)}`}>
                    {prettyStatus(row.status)}
                  </span>
                </td>

                <td className="px-6 py-4 text-xs text-neutral-500 whitespace-nowrap">
                  {row.created_at ? moment(row.created_at).format("MMMM D, YYYY") : "-"}
                </td>

                <td className="px-6 py-4 text-xs font-medium text-right whitespace-nowrap" />
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Pagination links={paged.links} from={paged.from} to={paged.to} total={paged.total} />

      {/* =========================
          MODAL DETAIL
         ========================= */}
      {isDetailOpen && (
        <div className="fixed inset-0 z-50 bg-black/40">
          <div className="absolute inset-0" onClick={closeDetail} />
          <div className="absolute inset-0 flex items-center justify-center p-4">
            <div className="w-full max-w-3xl rounded-2xl bg-white shadow-xl overflow-hidden">
              {/* header */}
              <div className="flex items-start justify-between px-5 py-4 border-b">
                <div className="space-y-1">
                  <div className="text-sm font-semibold text-neutral-900">
                    Approval Detail {detail?.request_number ? `#${detail.request_number}` : ""}
                  </div>
                  <div className="text-xs text-neutral-500">
                    {detail?.requested_at ? moment(detail.requested_at).format("MMMM D, YYYY HH:mm") : "-"}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={closeDetail}
                  className="p-2 rounded-lg hover:bg-neutral-100"
                  aria-label="Close"
                >
                  ✕
                </button>
              </div>

              {/* body */}
              <div className="p-5 max-h-[70vh] overflow-y-auto">
                {loadingDetail && <div className="text-xs text-neutral-500">Loading detail...</div>}
                {!loadingDetail && !detail && <div className="text-xs text-neutral-500">Detail tidak tersedia.</div>}

                {!loadingDetail && detail && (
                  <div className="space-y-5">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${statusColor(detail.status)}`}>
                        {prettyStatus(detail.status)}
                      </span>
                      <span className="text-xs text-neutral-500">
                        Type: <span className="text-neutral-900 font-medium">{String(detail.approvable_type ?? "").split("\\").pop() || "-"}</span>
                      </span>
                      <span className="text-xs text-neutral-500">
                        Current: <span className="text-neutral-900 font-medium">{stepBadge(detail.current_step_no)}</span>
                      </span>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                      <div className="rounded-xl border p-3">
                        <div className="text-neutral-500">Requester</div>
                        <div className="font-medium text-neutral-900">{detail.requested_by_name ?? "-"}</div>
                        <div className="text-neutral-500 mt-1">Outlet: {detail.outlet_id ?? "-"}</div>
                      </div>

                      <div className="rounded-xl border p-3">
                        <div className="text-neutral-500">Document</div>
                        <div className="font-medium text-neutral-900 break-all">{detail.approvable_id ?? "-"}</div>
                        <div className="text-neutral-500 mt-1 break-all">{detail.approvable_type ?? "-"}</div>
                      </div>
                    </div>

                    <div className="rounded-xl border p-3 text-xs">
                      <div className="font-semibold text-neutral-900 mb-2">Title & Summary</div>
                      <div className="text-neutral-900 font-medium">{detail.title ?? "-"}</div>
                      <div className="text-neutral-500 mt-1 whitespace-pre-wrap">{detail.summary ?? "-"}</div>
                    </div>

                    {/* Steps */}
                    <div className="rounded-xl border overflow-hidden">
                      <div className="px-3 py-2 bg-neutral-50 text-xs font-semibold text-neutral-900">
                        Steps ({detail.steps?.length ?? 0})
                      </div>
                      <div className="divide-y">
                        {(detail.steps ?? []).map((s) => (
                          <div key={s.id} className="p-3 text-xs">
                            <div className="flex items-center justify-between gap-3">
                              <div className="font-medium text-neutral-900">
                                Step {s.step_no}: {s.name}
                              </div>
                              <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${statusColor(s.status)}`}>
                                {prettyStatus(s.status)}
                              </span>
                            </div>
                            <div className="mt-1 text-neutral-500">
                              Rule: {s.rule_type} = {s.rule_value} • Min Approver: {s.min_approver}
                            </div>
                            {s.decided_by_name && (
                              <div className="mt-1 text-neutral-500">
                                By: <span className="text-neutral-900 font-medium">{s.decided_by_name}</span>
                                {s.decided_at ? ` • ${moment(s.decided_at).format("MMMM D, YYYY HH:mm")}` : ""}
                              </div>
                            )}
                            {s.note && <div className="mt-1 text-neutral-500 whitespace-pre-wrap">{s.note}</div>}
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="rounded-xl border overflow-hidden">
                      <div className="px-3 py-2 bg-neutral-50 text-xs font-semibold text-neutral-900">
                        Logs ({detail.actions?.length ?? 0})
                      </div>
                      <div className="divide-y">
                        {(detail.actions ?? []).map((a) => (
                          <div key={a.id} className="p-3 text-xs">
                            <div className="flex items-center justify-between">
                              <div className="font-medium text-neutral-900">
                                {a.action} {a.step_no ? `(Step ${a.step_no})` : ""}
                              </div>
                              <div className="text-neutral-500">
                                {a.created_at ? moment(a.created_at).format("MMM D, YYYY HH:mm") : ""}
                              </div>
                            </div>
                            <div className="text-neutral-500">
                              Actor: <span className="text-neutral-900 font-medium">{a.actor_name}</span>
                            </div>
                            {a.note && <div className="text-neutral-500 mt-1 whitespace-pre-wrap">{a.note}</div>}
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* meta_json snapshot_json (optional preview) */}
                    {(detail.meta_json || detail.snapshot_json) && (
                      <div className="rounded-xl border p-3 text-xs">
                        <div className="font-semibold text-neutral-900 mb-2">Meta / Snapshot</div>
                        <pre className="text-[11px] bg-neutral-50 border rounded-xl p-3 overflow-auto">
{JSON.stringify({ meta: detail.meta_json, snapshot: detail.snapshot_json }, null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* footer actions */}
              <div className="px-5 py-4 border-t flex items-center justify-between gap-3">
                <div className="text-xs text-neutral-500">
                  Actor: <span className="text-neutral-900 font-medium">{actorName}</span>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={closeDetail}
                    className="px-4 py-2 text-xs rounded-xl border border-neutral-200 hover:bg-neutral-50"
                  >
                    Close
                  </button>

                  {/* Cancel: biasanya untuk requester. Backend sudah enforce requester-only */}
                  {canAction && (
                    <button
                      type="button"
                      disabled={actionLoading}
                      onClick={onCancel}
                      className="px-4 py-2 text-xs rounded-xl border border-neutral-200 hover:bg-neutral-50 disabled:opacity-60"
                    >
                      {actionLoading ? "Processing..." : "Cancel"}
                    </button>
                  )}

                  {canAction && (
                    <button
                      type="button"
                      disabled={actionLoading}
                      onClick={onReject}
                      className="px-4 py-2 text-xs rounded-xl bg-red-600 text-white hover:bg-red-700 disabled:opacity-60"
                    >
                      {actionLoading ? "Processing..." : "Reject"}
                    </button>
                  )}

                  {canAction && (
                    <button
                      type="button"
                      disabled={actionLoading}
                      onClick={onApprove}
                      className="px-4 py-2 text-xs rounded-xl bg-green-600 text-white hover:bg-green-700 disabled:opacity-60"
                    >
                      {actionLoading ? "Processing..." : "Approve"}
                    </button>
                  )}
                </div>
              </div>

            </div>
          </div>
        </div>
      )}
    </div>
  )
}
